import React, { useContext, useEffect, useState, useRef } from "react";
import { AuthContext } from "../../context/authContext";
import { TITLE, VIDEO_DESCRIPTION, VIDEO_TITLE } from "../../config/config";
import Body from "../common/Body";
import { ThreadVideoContext, ThreadVideoProvider } from "../../context/threadVideoContext";
import { VideoItemContext, VideoItemProvider } from "../../context/videoItemContext";
import LoadingContent from "../common/LoadingContent";
import ErrorMessage from "../common/ErrorMessage";
import VideoItemMobile from "../Video/section/VideoItemMobile";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const VideoListMobile = ({ videos, loading, error, hasMore, onLoadMore }) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [isCommentListVisible, setCommentListVisible] = useState(false);
	const swiperRef = useRef(null);

	useEffect(() => {
		if (swiperRef.current) {
			swiperRef.current.swiper.allowTouchMove = !isCommentListVisible;
		}
	}, [isCommentListVisible]);

	const handleOpenCommentList = () => {
		setCommentListVisible(true);
	};

	const handleCloseCommentList = () => {
		setCommentListVisible(false);
	};

	if (error) {
		return <ErrorMessage message={error} />;
	}

	if (videos.length === 0 && loading) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<LoadingContent />
			</div>
		);
	}

	if (videos.length === 0 && !loading) {
		return (
			<div className="w-full h-full flex items-center justify-center">
				<p className="text-white text-center">No videos found</p>
			</div>
		);
	}

	return (
		<div id="video-list-scrollable" className="w-full h-full">
			<Swiper
				ref={swiperRef}
				className="h-full w-full swiper-initialized swiper-vertical swiper-backface-hidden"
				direction="vertical"
				slidesPerView={1}
				mousewheel={true}
				onSlideChange={(swiper) => {
					setActiveIndex(swiper.activeIndex);
					const videoElements = document.querySelectorAll(".swiper-slide video");
					videoElements.forEach((video, index) => {
						if (index !== swiper.activeIndex) {
							video.pause();
						} else {
							video.play().catch(e => console.log("Auto-play prevented", e));
						}
					});
				}}
				onReachEnd={() => {
					if (hasMore && !loading) {
						onLoadMore();
					}
				}}
			>
				{videos.map((video, index) => (
					<SwiperSlide key={video.video_id || index} className="h-full">
						<VideoItemMobile
							video={video}
							isActive={index === activeIndex}
							onOpenCommentList={handleOpenCommentList}
							onCloseCommentList={handleCloseCommentList}
							isCommentListVisible={isCommentListVisible}
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);
};

const ThreadHeader = ({ thread }) => {
	const handleTitleClick = () => {
		if (thread && thread.view_url) {
			window.location.href = thread.view_url;
		}
	};

	if (!thread) return null;

	return (
		<div
			className="px-2 pt-2 z-10 absolute top-0 left-0 right-0 bg-black bg-opacity-50 cursor-pointer active:bg-opacity-70 transition-opacity"
			onClick={handleTitleClick}
		>
			<section className="flex items-center mb-2">
				<div className="w-full">
					<h1 className="text-lg font-medium text-white flex items-center">
						{thread.title || VIDEO_TITLE}
						<svg
							className="ml-1 w-4 h-4 text-gray-300"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
							/>
						</svg>
					</h1>
					<p className="text-sm text-url pr-6">
						{thread.excerpt || VIDEO_DESCRIPTION}
					</p>
				</div>
			</section>
		</div>
	);
};

const MainContent = () => {
	const { visitor } = useContext(AuthContext);
	const { video: initialVideo } = useContext(VideoItemContext);
	const {
		thread,
		threadLoading,
		threadError,
		videosData,
		loadMoreVideos,
		fetchVideos
	} = useContext(ThreadVideoContext);

	useEffect(() => {
		// Load the mobile-specific CSS
		import("../../assets/video_mobile.css").catch(err =>
			console.error("Failed to load CSS:", err)
		);

		return () => {
			const link = document.querySelector('link[href*="video_mobile.css"]');
			if (link) {
				link.parentNode.removeChild(link);
			}
		};
	}, []);

	// Update document metadata
	useEffect(() => {
		if (thread) {
			document.title = `Videos của ${thread.title} | ${VIDEO_TITLE} | ${TITLE}`;
			const metaDescription = document.querySelector('meta[name="description"]');
			if (metaDescription) {
				metaDescription.setAttribute("content", thread.excerpt);
			}
		} else {
			document.title = `${VIDEO_TITLE} | ${TITLE}`;
			const metaDescription = document.querySelector('meta[name="description"]');
			if (metaDescription) {
				metaDescription.setAttribute("content", VIDEO_DESCRIPTION);
			}
		}
	}, [thread]);

	// Initial video fetch
	useEffect(() => {
		if (thread && !videosData.initialLoaded) {
			fetchVideos(true);
		}
	}, [thread, videosData.initialLoaded, fetchVideos]);

	// Initial data fetch if no thread is loaded yet
	useEffect(() => {
		if (!thread && !threadLoading) {
			fetchVideos(true);
		}
	}, [thread, threadLoading, fetchVideos]);

	if (threadError) {
		return (
			<div className="p-4 text-red-500 text-center">
				Error loading thread: {threadError}
			</div>
		);
	}

	if (!visitor || !visitor.can_view_videos) {
		return (
			<div className="p-4 text-white text-xl text-center">
				Vui lòng đăng nhập để xem videos
			</div>
		);
	}

	// Even if thread is loading, we can show the video container with loading state
	return (
		<div className="video-container w-full h-full flex flex-col">
			<ThreadHeader thread={thread} />

			<div className="flex-grow h-full">
				<VideoListMobile
					videos={videosData.videos || []}
					loading={videosData.loading || threadLoading}
					error={videosData.error || threadError}
					hasMore={videosData.hasMore}
					onLoadMore={loadMoreVideos}
				/>
			</div>
		</div>
	);
};

const ThreadVideoIndexMobile = () => {
	return (
		<ThreadVideoProvider>
			<VideoItemProvider>
				<Body mainContent={<MainContent />} />
			</VideoItemProvider>
		</ThreadVideoProvider>
	);
};

export default ThreadVideoIndexMobile;